import { useId } from 'react'
// Dog.tsx imports this file for the component, and this file imports the props
// type back. That is deliberate: DogProps is Dog's public contract and belongs
// there, and a type-only import is erased at compile time, so there is no
// runtime cycle. Keep the `type` keyword on it.
import type { DogProps } from './Dog'
import { PAPER, PAPER_EDGE } from './Letter'
import { ART_TRANSFORM, BODY, INK } from './dogArt'
import { BODY_CLIP, PARTS } from './dogParts'

const COAT = '#e9b87c'
const INK_COLOUR = '#b07c3c'
const EYE = '#4a2f14'
/** Pale gold rather than white, to stay in palette against the cream page. */
const EYE_GLINT = '#fbf1de'

/**
 * A golden retriever in profile, facing left, split into animated parts.
 *
 * The artwork is a single traced line drawing (see dogArt.ts), so the parts are
 * not separate shapes - each one is the whole drawing clipped to a region (see
 * dogParts.ts) and rotated about its own pivot. Drawing the body last, with
 * matching holes cut out of it, hides every join.
 */
export function DrawnDog({ letterClassName, className }: DogProps) {
  // Ids have to be unique per instance and safe inside url(); useId's colons
  // are not.
  const uid = useId().replace(/:/g, '')
  const id = (name: string) => `dog-${name}-${uid}`

  const art = (
    <g transform={ART_TRANSFORM}>
      <path d={BODY} fill={COAT} />
      <path d={INK} fill={INK_COLOUR} />
    </g>
  )

  return (
    <svg viewBox="0 14 351 292" className={className} role="presentation" aria-hidden="true">
      <defs>
        <clipPath id={id('front')}>
          <path d={PARTS.frontLegs.clip} />
        </clipPath>
        <clipPath id={id('hind')}>
          <path d={PARTS.hindLegs.clip} />
        </clipPath>
        <clipPath id={id('tail')}>
          <path d={PARTS.tail.clip} />
        </clipPath>
        <clipPath id={id('body')} clipRule="evenodd">
          <path d={BODY_CLIP} clipRule="evenodd" />
        </clipPath>
      </defs>

      {/* transformBox: view-box so the pivots can be given in viewBox units.
          The rotation itself comes from the keyframes in animations.css. */}
      <g
        className="dog-tail"
        clipPath={`url(#${id('tail')})`}
        style={{ transformBox: 'view-box', transformOrigin: PARTS.tail.pivot }}
      >
        {art}
      </g>
      <g
        className="dog-leg dog-leg-trail"
        clipPath={`url(#${id('hind')})`}
        style={{ transformBox: 'view-box', transformOrigin: PARTS.hindLegs.pivot }}
      >
        {art}
      </g>
      <g
        className="dog-leg dog-leg-lead"
        clipPath={`url(#${id('front')})`}
        style={{ transformBox: 'view-box', transformOrigin: PARTS.frontLegs.pivot }}
      >
        {art}
      </g>
      {/* Body last, so it covers the top of every moving part. */}
      <g clipPath={`url(#${id('body')})`}>{art}</g>

      {/* The trace has no eye, so one is drawn here. These are viewBox
          coordinates, not the artwork's flipped space, so it sits outside the
          ART_TRANSFORM group: just behind the stop and forward of the ear. */}
      <circle cx="43" cy="43" r="4.4" fill={EYE} />
      <circle cx="44.6" cy="41.2" r="1.5" fill={EYE_GLINT} />

      {/* The letter, gripped in the jaws. */}
      {letterClassName !== undefined && (
        <g className={letterClassName} transform="rotate(-8 34 103)">
          <rect
            x="1"
            y="82"
            width="66"
            height="43"
            rx="4"
            fill={PAPER}
            stroke={PAPER_EDGE}
            strokeWidth="4"
          />
          <path d="M1 85 L34 106 L67 85" fill="none" stroke={PAPER_EDGE} strokeWidth="4" />
        </g>
      )}
    </svg>
  )
}
